import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Controller } from "react-hook-form";
import type { UseFormTrigger } from "react-hook-form";
import {
  dxtToLocalServerTime,
  getMomentTz,
  TimeService,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { Moment } from "moment";
import type { TaskHistoryEntry } from "../../models";
import { getTaskHistory } from "../../repositories/SharedEqpRepository";

const moment = getMomentTz();

interface TaskDateSectionProps {
  /** react-hook-form control — parent form must have a `timestamp: Moment | null` field */
  control: any;
  /** react-hook-form trigger — used to revalidate when tasks load */
  trigger: UseFormTrigger<any>;
  /** PartId to load task history for. NULL hides the section. */
  partId: number | null;
  /** Whether the modal is open — triggers data load. */
  open: boolean;
  /** Action type controls validation rules. Repair has no "after last task" constraint. */
  action: "install" | "uninstall" | "repair";
  /**
   * Pre-computed last task timestamp for the part being installed (known from tree).
   * If provided, we skip the API call for this part.
   */
  installedPartLastTimestamp?: Date | null;
  /**
   * PartId of the occupant being displaced in a swap operation.
   * When provided, we call the API to get its last task timestamp.
   */
  occupantPartId?: number | null;
  /**
   * Last task timestamp on the target location (ParentPartId + PositionId).
   * When provided, install date must be after this to prevent overlapping parts.
   * This is the timestamp of the last part that was uninstalled from the location.
   */
  locationLastTimestamp?: Date | null;
  /**
   * Whether to validate against CASTER_EQP_INITIAL_DATETIME setting.
   * Only applicable for Caster mold module. Defaults to false.
   */
  validateAgainstInitialDatetime?: boolean;
}

/**
 * Reusable section for Install/Uninstall/Repair modals.
 * Renders: info alert + DateTimePicker (Controller) + task history table.
 *
 * The DateTimePicker is a react-hook-form Controller field named "timestamp".
 * Parent forms must include `timestamp: Moment | null` in their FormData.
 *
 * Validation:
 * - Install/Uninstall: must be after the last task timestamp and before now
 * - Repair: must not be in the future (no lower bound)
 * - Swap: must be after the most recent last-task of BOTH parts
 * - CASTER_EQP_INITIAL_DATETIME: when validateAgainstInitialDatetime is true
 */
export const TaskDateSection = ({
  control,
  trigger,
  partId,
  open,
  action,
  installedPartLastTimestamp,
  occupantPartId,
  locationLastTimestamp,
  validateAgainstInitialDatetime = false,
}: TaskDateSectionProps) => {
  const [tasks, setTasks] = useState<TaskHistoryEntry[]>([]);
  const [occupantLastTimestamp, setOccupantLastTimestamp] =
    useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { state: utlState } = useUTLSettingsContext();

  // Get CASTER_EQP_INITIAL_DATETIME from settings context (default: 2026-01-01T00:00:00.000Z)
  const casterInitialDatetime = useMemo(() => {
    if (!validateAgainstInitialDatetime) return null;
    const settingValue = get(
      utlState.settings,
      "CASTER_EQP_INITIAL_DATETIME",
      "2026-01-01T00:00:00.000Z",
    );
    return moment.utc(settingValue as string).toDate();
  }, [utlState.settings, validateAgainstInitialDatetime]);

  const loadTasks = useCallback(async () => {
    if (!partId) {
      setTasks([]);
      return;
    }
    setIsLoading(true);
    try {
      // Load task history for the selected part (from selector)
      const partTasks = await getTaskHistory(partId);
      // Only load occupant if there's an occupant (swap scenario)
      const occupantTasks = occupantPartId
        ? await getTaskHistory(occupantPartId, 1)
        : [];
      setTasks(partTasks);
      setOccupantLastTimestamp(
        occupantTasks.length > 0 ? occupantTasks[0].Timestamp : null,
      );
    } finally {
      setIsLoading(false);
    }
  }, [partId, occupantPartId]);

  // Load tasks when opened or partId changes
  useEffect(() => {
    if (open && partId) {
      loadTasks();
    } else {
      setTasks([]);
      setOccupantLastTimestamp(null);
    }
  }, [open, partId, loadTasks]);

  // Re-validate timestamp when tasks load (constraints change)
  useEffect(() => {
    if (
      tasks.length > 0 ||
      installedPartLastTimestamp ||
      locationLastTimestamp ||
      occupantLastTimestamp ||
      casterInitialDatetime
    ) {
      trigger("timestamp");
    }
  }, [
    tasks,
    installedPartLastTimestamp,
    locationLastTimestamp,
    occupantLastTimestamp,
    casterInitialDatetime,
    trigger,
  ]);

  // Derive constraints — use preloaded for installed part, otherwise from tasks (selected part)
  // Parse with moment.utc to avoid timezone shifts (same pattern as getTaskHistory)
  const partLastTimestamp =
    installedPartLastTimestamp !== undefined
      ? moment.utc(installedPartLastTimestamp)
      : tasks.length > 0
        ? moment.utc(tasks[0].Timestamp)
        : null;

  // For repair: use first task timestamp (oldest task) as lower bound
  const partFirstTimestamp =
    tasks.length > 0 ? moment.utc(tasks[tasks.length - 1].Timestamp) : null;

  // Use the most recent of all lower bounds (part, location, occupant, casterInitialDatetime)
  const effectiveConstraint = (() => {
    const candidates: Moment[] = [];
    if (partLastTimestamp) candidates.push(partLastTimestamp);
    if (locationLastTimestamp)
      candidates.push(moment.utc(locationLastTimestamp));
    if (occupantLastTimestamp)
      candidates.push(moment.utc(occupantLastTimestamp));
    if (casterInitialDatetime)
      candidates.push(moment.utc(casterInitialDatetime));
    if (candidates.length === 0) return null;
    return candidates.reduce((max, c) => (c.isAfter(max) ? c : max));
  })();

  // Identify where the constraint comes from for the alert message
  const constraintFromLocation = !!(
    locationLastTimestamp &&
    effectiveConstraint &&
    moment.utc(locationLastTimestamp).isSame(effectiveConstraint)
  );
  const constraintFromOccupant = !!(
    !constraintFromLocation &&
    occupantLastTimestamp &&
    effectiveConstraint &&
    moment.utc(occupantLastTimestamp).isSame(effectiveConstraint)
  );
  const constraintFromCasterFloor = !!(
    !constraintFromLocation &&
    !constraintFromOccupant &&
    casterInitialDatetime &&
    effectiveConstraint &&
    moment.utc(casterInitialDatetime).isSame(effectiveConstraint)
  );
  const constraintFromPart = !!(
    effectiveConstraint &&
    !constraintFromLocation &&
    !constraintFromOccupant &&
    !constraintFromCasterFloor
  );

  // For repair: MAX(first task, casterInitialDatetime)
  const repairLowerBound = (() => {
    if (action !== "repair") return null;
    const candidates: Moment[] = [];
    if (partFirstTimestamp) candidates.push(partFirstTimestamp);
    if (casterInitialDatetime)
      candidates.push(moment.utc(casterInitialDatetime));
    if (candidates.length === 0) return null;
    return candidates.reduce((max, c) => (c.isAfter(max) ? c : max));
  })();

  // Validation function for the Controller
  const validateTimestamp = (value: Moment | null): string | true => {
    if (!value || !value.isValid()) return "Invalid date";
    if (value.isAfter(moment())) return "Date cannot be in the future";

    // Repair: must be after MAX(first task, casterInitialDatetime)
    if (action === "repair" && repairLowerBound) {
      if (value.isSameOrBefore(repairLowerBound)) {
        const source =
          casterInitialDatetime &&
          moment.utc(casterInitialDatetime).isSame(repairLowerBound)
            ? "initial date limit"
            : "first task";
        return `Date must be after ${source} (${dxtToLocalServerTime(repairLowerBound.toDate(), "MM/dd/yyyy HH:mm")})`;
      }
    }

    // Install/Uninstall: must be after MAX(last task, location, occupant, casterInitialDatetime)
    if (action !== "repair" && effectiveConstraint) {
      if (value.isSameOrBefore(effectiveConstraint)) {
        if (constraintFromLocation) {
          return `Date must be after the previous part on this location (${dxtToLocalServerTime(effectiveConstraint.toDate(), "MM/dd/yyyy HH:mm")})`;
        }
        if (constraintFromOccupant) {
          return "Date must be after the occupant's last task. Adjust its date first if needed.";
        }
        const source =
          casterInitialDatetime &&
          moment.utc(casterInitialDatetime).isSame(effectiveConstraint)
            ? "initial date limit"
            : "last task";
        return `Date must be after ${source} (${dxtToLocalServerTime(effectiveConstraint.toDate(), "MM/dd/yyyy HH:mm")})`;
      }
    }

    return true;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size="1.5rem" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Info alert */}
      {action === "repair" ? (
        <Alert severity="info" variant="outlined">
          {repairLowerBound ? (
            <>
              Must be after{" "}
              <strong>
                {dxtToLocalServerTime(
                  repairLowerBound.toDate(),
                  "MM/dd/yyyy HH:mm",
                )}
              </strong>
              {" and before "}
              <strong>now</strong>
            </>
          ) : (
            "Repair can be placed at any date (must not be in the future)."
          )}
        </Alert>
      ) : (
        <Alert
          severity={
            constraintFromOccupant || constraintFromLocation
              ? "warning"
              : "info"
          }
          variant="outlined"
        >
          {effectiveConstraint ? (
            <>
              Must be after{" "}
              <strong>
                {dxtToLocalServerTime(
                  effectiveConstraint.toDate(),
                  "MM/dd/yyyy HH:mm",
                )}
              </strong>
              {constraintFromLocation
                ? " (previous part on this location)"
                : constraintFromOccupant
                  ? " (from the part being displaced)"
                  : constraintFromCasterFloor
                    ? " (initial date limit)"
                    : constraintFromPart
                      ? " (part's last task)"
                      : ""}
              {" and before "}
            </>
          ) : (
            <>Must be before </>
          )}
          <strong>now</strong>
        </Alert>
      )}

      {/* DateTimePicker via react-hook-form Controller */}
      <Controller
        name="timestamp"
        control={control}
        rules={{ validate: validateTimestamp }}
        render={({ field, fieldState: { error } }) => (
          <DateTimePicker
            value={field.value}
            onChange={field.onChange}
            timezone={TimeService.getInstance().getServerTimeZone()}
            format="MM/DD/YYYY HH:mm"
            ampm={false}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: !!error,
                helperText: error?.message ?? " ",
              },
            }}
          />
        )}
      />

      {/* Task history table — shows the SELECTED part's tasks */}
      {tasks.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Location</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task.TaskId} sx={{ opacity: 0.7 }}>
                <TableCell>
                  {dxtToLocalServerTime(task.Timestamp, "MM/dd/yyyy HH:mm")}
                </TableCell>
                <TableCell>{task.Action}</TableCell>
                <TableCell>{task.LocationName ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : installedPartLastTimestamp ? (
        <Typography variant="body2" color="text.secondary">
          Last task:{" "}
          {dxtToLocalServerTime(installedPartLastTimestamp, "MM/dd/yyyy HH:mm")}
        </Typography>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No previous tasks found.
        </Typography>
      )}
    </Box>
  );
};
